import { useEffect, useMemo, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { UserRound } from 'lucide-react';
import type { User, UserCreateRequest, UserUpdateRequest } from '@cortex/domain';

import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Separator } from '@/components/ui/shadcn/separator';
import { FormField } from '@/components/ui/FormField';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge, toneForLifecycleStatus } from '@/components/ui/StatusBadge';

/**
 * Detail pane for the Users tab — handles both create ("invite") and
 * edit modes.
 *
 * Authentication is owned by Cloudflare Access, so creating a user does
 * NOT set a password here. We capture email + name + company; the person
 * is linked automatically the first time they sign in through Cloudflare
 * with that email. Two fields are immutable after creation and rendered
 * read-only in edit mode: email (the Cloudflare binding key) and company.
 *
 * Form state + validation use React Hook Form + Zod, mirroring
 * EntityDetail. Save is disabled in edit mode until something changes,
 * so we never fire an empty PATCH.
 */

export interface EntityOption {
  readonly id: string;
  readonly name: string;
}

export interface UserDetailProps {
  readonly mode: 'idle' | 'create' | 'edit';
  readonly user: User | null;
  /** Companies a global admin can place a new user in. Empty for own-entity admins. */
  readonly entityOptions: readonly EntityOption[];
  /** When the caller can only manage their own entity, this locks the company picker. */
  readonly lockedEntity: EntityOption | null;
  readonly isSaving: boolean;
  readonly errorMessage?: string | null;
  /**
   * Roles editor, injected by the controller (which owns the query/toast
   * providers). Rendered only in edit mode. Kept as a slot so UserDetail
   * stays a pure presentational form with no network dependency.
   */
  readonly rolesSlot?: ReactNode;
  /**
   * Authority override panel (PR #5c). Injected by the controller which
   * owns the authority query and mutations. Rendered only in edit mode.
   */
  readonly authoritySlot?: ReactNode;
  readonly onCreate: (input: UserCreateRequest) => Promise<void>;
  readonly onUpdate: (id: string, input: UserUpdateRequest) => Promise<void>;
  readonly onCancel: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

interface UserFormValues {
  entityId: string;
  email: string;
  displayName: string;
  status: 'Active' | 'Suspended';
}

const CreateFormSchema = z.object({
  entityId: z.string().min(1, 'Company is required.'),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required.')
    .refine((v) => EMAIL_RE.test(v.trim()), 'Enter a valid email address.'),
  displayName: z.string().trim().min(1, 'Full name is required.'),
  status: z.enum(['Active', 'Suspended']),
});

// In edit mode email + company are read-only, so they pass through
// without validation; only name + status are editable.
const EditFormSchema = z.object({
  entityId: z.string(),
  email: z.string(),
  displayName: z.string().trim().min(1, 'Full name is required.'),
  status: z.enum(['Active', 'Suspended']),
});

const emptyForm = (entityId: string): UserFormValues => ({
  entityId,
  email: '',
  displayName: '',
  status: 'Active',
});

const fromUser = (user: User): UserFormValues => ({
  entityId: user.entityId,
  email: user.email,
  displayName: user.displayName,
  status: user.status,
});

const clean = (s: string): string => s.trim();

function toCreateRequest(v: UserFormValues): UserCreateRequest {
  return {
    entityId: v.entityId,
    email: clean(v.email),
    displayName: clean(v.displayName),
    status: v.status,
  };
}

function toUpdateRequest(v: UserFormValues, user: User): UserUpdateRequest {
  const patch: Record<string, unknown> = {};
  if (clean(v.displayName) !== user.displayName) patch['displayName'] = clean(v.displayName);
  if (v.status !== user.status) patch['status'] = v.status;
  return patch as UserUpdateRequest;
}

const PROVIDER_LABELS: Readonly<Record<User['identityProvider'], string>> = {
  'cloudflare-otp': 'Cloudflare one-time PIN',
  'entra-id': 'Microsoft Entra ID',
  google: 'Google',
  'service-account': 'Service account',
};

export function UserDetail({
  mode,
  user,
  entityOptions,
  lockedEntity,
  isSaving,
  errorMessage,
  rolesSlot,
  authoritySlot,
  onCreate,
  onUpdate,
  onCancel,
}: UserDetailProps) {
  const defaultEntityId = useMemo(
    () => lockedEntity?.id ?? entityOptions[0]?.id ?? '',
    [lockedEntity, entityOptions],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(mode === 'create' ? CreateFormSchema : EditFormSchema),
    defaultValues: mode === 'edit' && user ? fromUser(user) : emptyForm(defaultEntityId),
  });

  useEffect(() => {
    if (mode === 'edit' && user) reset(fromUser(user));
    else if (mode === 'create') reset(emptyForm(defaultEntityId));
  }, [mode, user, defaultEntityId, reset]);

  const values = watch();
  const dirty =
    mode === 'create' || (user ? Object.keys(toUpdateRequest(values, user)).length > 0 : false);

  const onSubmit = handleSubmit(async (data) => {
    if (mode === 'create') await onCreate(toCreateRequest(data));
    else if (user) await onUpdate(user.id, toUpdateRequest(data, user));
  });

  if (mode === 'idle') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          icon={UserRound}
          title="Select a user to view details."
          description="Or click “Invite user” on the left to add one."
        />
      </div>
    );
  }

  const lockedEntityName =
    mode === 'create'
      ? lockedEntity?.name
      : (entityOptions.find((o) => o.id === user?.entityId)?.name ?? user?.entityName);

  return (
    // `noValidate`: RHF + Zod own validation so messages stay consistent
    // and native browser popups don't pre-empt our submit on type="email".
    <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-start justify-between border-b border-border bg-card px-5 py-4">
        <div>
          <h2 className="text-sm font-bold text-foreground">
            {mode === 'create' ? 'Invite user' : user?.displayName}
          </h2>
          {mode === 'edit' && user && (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{user.entityName}</span>
              <span className="text-xs text-border">·</span>
              <StatusBadge tone={toneForLifecycleStatus(user.status)} label={user.status} />
            </div>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {errorMessage && (
          <div
            className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {mode === 'create' && (
          <div className="rounded-md border border-info/20 bg-info/10 px-3 py-2 text-micro text-info">
            This creates the CORTEX record only. The person signs in through Cloudflare Access using
            this email address — their login is linked automatically on first sign-in. No password
            is set here.
          </div>
        )}

        <FieldSection title="Identity">
          {mode === 'create' ? (
            <>
              <FormField
                label="Company"
                required
                error={errors.entityId?.message}
                hint={lockedEntity ? 'Users can only be added to your own company.' : undefined}
              >
                {lockedEntity ? (
                  <Input type="text" value={lockedEntity.name} disabled readOnly />
                ) : (
                  <select className={SELECT_CLASS} disabled={isSaving} {...register('entityId')}>
                    {entityOptions.length === 0 && <option value="">No companies available</option>}
                    {entityOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
              <FormField
                label="Email address"
                required
                error={errors.email?.message}
                hint="Must match the email the person uses to sign in to Cloudflare Access."
              >
                <Input type="email" disabled={isSaving} {...register('email')} />
              </FormField>
            </>
          ) : (
            <ReadOnlyField label="Email address" value={user?.email ?? '—'} mono />
          )}
          <FormField label="Full name" required error={errors.displayName?.message}>
            <Input type="text" disabled={isSaving} {...register('displayName')} />
          </FormField>
          {mode === 'edit' && <ReadOnlyField label="Company" value={lockedEntityName ?? '—'} />}
        </FieldSection>

        <Separator />

        <FieldSection title="Lifecycle">
          <FormField label="Status" hint="Suspended users keep their record but cannot sign in.">
            <select className={SELECT_CLASS} disabled={isSaving} {...register('status')}>
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
            </select>
          </FormField>
        </FieldSection>

        {mode === 'edit' && user && (
          <>
            {rolesSlot && (
              <>
                <Separator />
                {rolesSlot}
              </>
            )}

            {authoritySlot && (
              <>
                <Separator />
                {authoritySlot}
              </>
            )}

            <Separator />
            <FieldSection title="Sign-in">
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ReadOnlyField
                  label="Cloudflare link"
                  value={user.cfAccessSubject ? 'Linked' : 'Not yet signed in'}
                />
                <ReadOnlyField
                  label="Identity provider"
                  value={PROVIDER_LABELS[user.identityProvider]}
                />
                <ReadOnlyField
                  label="Last sign-in"
                  value={user.lastLoginAt ? formatTimestamp(user.lastLoginAt) : 'Never'}
                />
              </dl>
            </FieldSection>
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/40 px-5 py-3">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSaving || !dirty}>
          {isSaving ? 'Saving…' : mode === 'create' ? 'Invite user' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';

function FieldSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ReadOnlyField({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium text-foreground">{label}</dt>
      <dd
        className={
          mono ? 'font-mono text-xs text-muted-foreground' : 'text-xs text-muted-foreground'
        }
      >
        {value}
      </dd>
    </div>
  );
}

const formatTimestamp = (iso: string): string => {
  try {
    return format(parseISO(iso), 'd MMM yyyy, HH:mm');
  } catch {
    return iso;
  }
};
